import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { BonusIcon, Separator, WalletIcon } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatSum } from 'utils';

import s from './Deposit.module.scss';
import {
  IDepositMoneyProps,
  IGetDepositDataRequest,
  IGetDepositDataResponse,
} from './interfaces';
import { GET_DEPOSIT_DATA } from './schema';

export const Deposit: FC<IDepositMoneyProps> = ({ workShiftId, small }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const isBonusesAvailable = useIsFeatureAvailable('bonus_cash');

  const { data: workshiftMoneyData } = useQuery<
    IGetDepositDataResponse,
    IGetDepositDataRequest
  >(GET_DEPOSIT_DATA, {
    fetchPolicy: 'cache-only',
    skip: !permissions?.has('work_shift_active'),
    variables: { id: workShiftId },
  });

  const { bonus, deposit } =
    workshiftMoneyData?.getWorkShiftPaymentOverviewData ?? {};

  return (
    <div className={s.money_block}>
      <FeatureHOC
        alias="bonus_cash"
        strategy="violetReplace"
        variant="secondary"
        hideTitle
      >
        <div
          className={cn(
            s.money,
            small && s.small,
            !isBonusesAvailable && s.split
          )}
        >
          <div className={cn(s.block)}>
            <span className={s.title}>
              <BonusIcon size="m" color="warning" />
              {t('category.bonus')}
            </span>
            <span>
              {formatSum(bonus, currency.sign, { maximumFractionDigits: 2 })}
            </span>
          </div>
          <Separator className={s.separator} />
          <div className={cn(s.block, s.deposit)}>
            {small && (
              <>
                <span className={s.title}>
                  <WalletIcon size="m" color="violet" />
                  {t('category.deposits.spend')}
                </span>
                <span>
                  {formatSum(deposit, currency.sign, {
                    maximumFractionDigits: 2,
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </FeatureHOC>
    </div>
  );
};
